/**
 * @fileoverview Pantalla Gigante de Estadio para el módulo "Arma la Playlist" (Id = 45) en Interaktia-V2.
 * 
 * Diseñado para pantallas de gran formato (1080p / 4K) en estadios y arenas con renderizado fluido a 60 FPS,
 * animaciones aceleradas por GPU, alto contraste y sincronización en tiempo real vía SignalR/WebSocket.
 * 
 * Soporta 3 modos visuales configurables en cabina de sonido:
 * 1. CallToAction (Convocatoria masiva con QR gigante y animación envolvente).
 * 2. NowPlaying (Canción sonando en vivo con vinilo giratorio/ecualizador y badges de aficionado/votos).
 * 3. Queue (Split layout con tema actual + próximas 3 canciones en la lista y QR de participación).
 * 
 * @module components/Screen/Sections/PlaylistScreen
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { GetPlaylistSettings, GetActivePlaylist } from '../../../utilities/DL/Admin/Overlays/Playlist';
import { PlaylistWS } from '../../../utilities/WS/Admin/PlaylistWS';

/**
 * Resuelve la URL absoluta para recursos multimedia cargados dinámicamente.
 * 
 * @param {string} mediaPath - Ruta relativa o URL absoluta.
 * @returns {string} URL completa formateada.
 */
const getFullAssetUrl = (mediaPath) => {
    if (!mediaPath) return '';
    if (
        mediaPath.startsWith('http://') ||
        mediaPath.startsWith('https://') ||
        mediaPath.startsWith('blob:') ||
        mediaPath.startsWith('data:')
    ) {
        return mediaPath;
    }
    const apiBaseUrl = (process.env.REACT_APP_API_URL || 'https://localhost:50586/api/')
        .replace('/api/', '/')
        .replace('/api', '/');
    const cleanBase = apiBaseUrl.endsWith('/') ? apiBaseUrl : `${apiBaseUrl}/`;
    return `${cleanBase}${mediaPath.startsWith('/') ? mediaPath.substring(1) : mediaPath}`;
};

/**
 * Configuración predeterminada de Arma la Playlist para la pantalla.
 */
const DEFAULT_SETTINGS = {
    screenMode: 1, // 1: Convocatoria, 2: Now Playing, 3: En Cola
    ctaTitle: '¡ARMA LA PLAYLIST!',
    ctaSubtitle: '¡Escanea el código con tu celular y elige las canciones del estadio!',
    showFanNameOnScreen: true,
    showVoteCountOnScreen: true,
    isScreenQrVisible: true,
    screenBackgroundUrl: '',
    qrCenterLogoUrl: ''
};

/**
 * Componente principal de la Pantalla de Estadio para Arma la Playlist.
 * 
 * @component
 * @param {Object} props
 * @param {string|null} [props.roomId=null] - Identificador GUID de la sala.
 */
export const PlaylistScreen = ({ roomId: propRoomId = null }) => {
    // 1. Resolver roomId efectivo desde props o query params
    const searchParams = useMemo(() => {
        return typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams();
    }, []);

    const effectiveRoomId = propRoomId || searchParams.get('roomId') || '';

    // 2. Estados reactivos de la dinámica
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [nowPlaying, setNowPlaying] = useState(null);
    const [activePlaylist, setActivePlaylist] = useState([]);
    const [connectionStatus, setConnectionStatus] = useState('idle');
    const [isLoading, setIsLoading] = useState(true);

    // 3. URL de destino para los códigos QR
    const qrTargetUrl = useMemo(() => {
        if (typeof window === 'undefined') return '';
        const origin = window.location.origin;
        return effectiveRoomId
            ? `${origin}/playlist?s=screen&roomId=${encodeURIComponent(effectiveRoomId)}`
            : `${origin}/playlist?s=screen`;
    }, [effectiveRoomId]);

    // 4. Carga inicial de datos (Configuración y Playlist Activa)
    const loadInitialData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [settingsRes, playlistRes] = await Promise.all([
                GetPlaylistSettings(effectiveRoomId),
                GetActivePlaylist(effectiveRoomId)
            ]);

            if (settingsRes && settingsRes.success && settingsRes.data) {
                setSettings(prev => ({
                    ...prev,
                    ...settingsRes.data,
                    screenMode: settingsRes.data.screenMode !== undefined ? Number(settingsRes.data.screenMode) : prev.screenMode
                }));
            }

            if (playlistRes && playlistRes.success && Array.isArray(playlistRes.data)) {
                setActivePlaylist(playlistRes.data);
                // Detectar si alguna pista ya está marcada como Now Playing
                const currentTrack = playlistRes.data.find(track => track.isNowPlaying === true);
                if (currentTrack) {
                    setNowPlaying(currentTrack);
                }
            }
        } catch (error) {
            console.error('[PlaylistScreen] Error al cargar configuración inicial:', error);
        } finally {
            setIsLoading(false);
        }
    }, [effectiveRoomId]);

    // 5. Suscripción y manejo en tiempo real con SignalR / WebSocket
    useEffect(() => {
        loadInitialData();

        // Configuración de handlers reactivos
        const wsHandlers = {
            now_playing_changed: (payload) => {
                if (!payload || (typeof payload === 'object' && !payload.id && !payload.suggestionId && !payload.title)) {
                    setNowPlaying(null);
                } else {
                    const trackData = payload.suggestion || payload.track || payload;
                    setNowPlaying(trackData);
                }
            },
            playlist_updated: (payload) => {
                const list = Array.isArray(payload)
                    ? payload
                    : (payload?.activePlaylist || payload?.playlist || payload?.items || []);
                setActivePlaylist(list);

                // Si no hay nowPlaying explícito, verificar si en la lista viene uno marcado
                const playingInList = list.find(t => t.isNowPlaying === true);
                if (playingInList) {
                    setNowPlaying(playingInList);
                }
            },
            settings_updated: (payload) => {
                if (payload) {
                    setSettings(prev => ({
                        ...prev,
                        ...payload,
                        screenMode: payload.screenMode !== undefined ? Number(payload.screenMode) : prev.screenMode
                    }));
                }
            },
            dynamic_reset: () => {
                setNowPlaying(null);
                setActivePlaylist([]);
            },
            vote_added: (payload) => {
                if (!payload) return;
                const suggestionId = payload.suggestionId || payload.id;
                const newVotes = payload.voteCount || payload.votes;

                // Actualizar Now Playing si corresponde
                setNowPlaying(prev => {
                    if (prev && (prev.id === suggestionId || prev.suggestionId === suggestionId)) {
                        return { ...prev, voteCount: newVotes !== undefined ? newVotes : (prev.voteCount || 0) + 1 };
                    }
                    return prev;
                });

                // Actualizar en la lista activa
                setActivePlaylist(prev => prev.map(track => {
                    if (track.id === suggestionId || track.suggestionId === suggestionId) {
                        return { ...track, voteCount: newVotes !== undefined ? newVotes : (track.voteCount || 0) + 1 };
                    }
                    return track;
                }));
            }
        };

        // Conectar al Hub de SignalR
        PlaylistWS.connect(effectiveRoomId, wsHandlers, (status) => {
            setConnectionStatus(status);
        });

        // Limpieza y desuscripción al desmontar
        return () => {
            PlaylistWS.disconnect();
        };
    }, [effectiveRoomId, loadInitialData]);

    // 6. Lista filtrada de próximas canciones en cola (Modo 3)
    const nextQueueTracks = useMemo(() => {
        const currentId = nowPlaying ? (nowPlaying.id || nowPlaying.suggestionId) : null;
        return activePlaylist
            .filter(t => !t.isPlayed && (currentId ? (t.id !== currentId && t.suggestionId !== currentId) : true))
            .slice(0, 3);
    }, [activePlaylist, nowPlaying]);

    // 7. Configuración de imagen excavada en el centro del QR
    const qrCenterLogoSettings = useMemo(() => {
        if (!settings.qrCenterLogoUrl) return undefined;
        return (qrSize) => ({
            src: getFullAssetUrl(settings.qrCenterLogoUrl),
            height: Math.round(qrSize * 0.22),
            width: Math.round(qrSize * 0.22),
            excavate: true
        });
    }, [settings.qrCenterLogoUrl]);

    // 8. Modo actual de pantalla
    const currentMode = Number(settings.screenMode) || 1;

    return (
        <div className="playlist-screen">
            {/* ========================================================================= */}
            {/* CAPA DE FONDO (IMAGEN PERSONALIZADA O GRADIENTE ESTADIO DE ALTA ENERGÍA) */}
            {/* ========================================================================= */}
            {settings.screenBackgroundUrl ? (
                <div
                    className="playlist-screen__bg"
                    style={{ backgroundImage: `url(${getFullAssetUrl(settings.screenBackgroundUrl)})` }}
                />
            ) : (
                <div className="playlist-screen__bg playlist-screen__bg--default" />
            )}

            {/* Overlay oscuro para legibilidad broadcast */}
            <div className="playlist-screen__overlay" />

            {/* Luces y haces de estadio dinámicos */}
            <div className="playlist-screen__beam playlist-screen__beam--left" />
            <div className="playlist-screen__beam playlist-screen__beam--right" />
            <div className="playlist-screen__stadium-glow" />

            {/* ========================================================================= */}
            {/* CABECERA: BRANDING Y STATUS ON-AIR */}
            {/* ========================================================================= */}
            <header className="playlist-screen__header">
                <div className="playlist-screen__brand">
                    <span className="playlist-screen__brand-pill">
                        <i className="bi bi-music-note-beamed me-2" />
                        INTERAKTIA DJ
                    </span>
                    <span className="playlist-screen__brand-sub">ESTADIO BROADCAST</span>
                </div>

                <div className="playlist-screen__live-indicator">
                    <span className="playlist-screen__live-dot" />
                    <span className="playlist-screen__live-text">EN VIVO</span>
                    <div className="playlist-screen__eq-bars playlist-screen__eq-bars--small ms-2">
                        <span className="playlist-screen__eq-bar" />
                        <span className="playlist-screen__eq-bar" />
                        <span className="playlist-screen__eq-bar" />
                    </div>
                </div>
            </header>

            {/* ========================================================================= */}
            {/* CONTENIDO PRINCIPAL SEGÚN MODO ACTIVO (1: CTA, 2: NOW PLAYING, 3: QUEUE) */}
            {/* ========================================================================= */}
            <main className="playlist-screen__main">
                {/* --------------------------------------------------------------------- */}
                {/* MODO 1: CALL TO ACTION (CONVOCATORIA Y QR GIGANTE)                     */}
                {/* --------------------------------------------------------------------- */}
                {currentMode === 1 && (
                    <div className="playlist-screen__cta">
                        <div className="playlist-screen__cta-content">
                            {/* Badges superiores de convocatoria */}
                            <div className="playlist-screen__cta-top-badge">
                                <i className="bi bi-soundwave text-cyan me-2" />
                                <span>TÚ CONTROLAS LA MÚSICA</span>
                            </div>

                            <h1 className="playlist-screen__cta-title">
                                {settings.ctaTitle || '¡ARMA LA PLAYLIST!'}
                            </h1>

                            <p className="playlist-screen__cta-subtitle">
                                {settings.ctaSubtitle || '¡Escanea el código con tu celular y elige las canciones del estadio!'}
                            </p>

                            {/* Contenedor del Código QR Gigante */}
                            <div className="playlist-screen__qr-box playlist-screen__qr-box--giant">
                                <div className="playlist-screen__qr-frame">
                                    <QRCodeSVG
                                        value={qrTargetUrl}
                                        size={360}
                                        bgColor="#ffffff"
                                        fgColor="#0a0f1d"
                                        level="H"
                                        imageSettings={qrCenterLogoSettings ? qrCenterLogoSettings(360) : undefined}
                                    />
                                </div>
                                <div className="playlist-screen__qr-pulse-ring" />
                            </div>

                            {/* Pasos rápidos para la afición */}
                            <div className="playlist-screen__steps">
                                <div className="playlist-screen__step">
                                    <span className="playlist-screen__step-num">1</span>
                                    <span className="playlist-screen__step-text">Abre tu cámara</span>
                                </div>
                                <div className="playlist-screen__step-arrow">
                                    <i className="bi bi-chevron-right" />
                                </div>
                                <div className="playlist-screen__step">
                                    <span className="playlist-screen__step-num">2</span>
                                    <span className="playlist-screen__step-text">Pide o vota canciones</span>
                                </div>
                                <div className="playlist-screen__step-arrow">
                                    <i className="bi bi-chevron-right" />
                                </div>
                                <div className="playlist-screen__step">
                                    <span className="playlist-screen__step-num">3</span>
                                    <span className="playlist-screen__step-text">¡Hazla sonar en vivo!</span>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* --------------------------------------------------------------------- */}
                {/* MODO 2: NOW PLAYING (TEMA EN REPRODUCCIÓN EN VIVO)                      */}
                {/* --------------------------------------------------------------------- */}
                {currentMode === 2 && (
                    <div className="playlist-screen__now-playing">
                        {nowPlaying ? (
                            <div className="playlist-screen__now-playing-grid">
                                {/* Tarjeta Hero Central de la Canción */}
                                <div className="playlist-screen__hero-card">
                                    <div className="playlist-screen__hero-header">
                                        <span className="playlist-screen__hero-pill">
                                            <i className="bi bi-disc-fill me-2" /> SONANDO EN VIVO
                                        </span>
                                        <div className="playlist-screen__eq-bars">
                                            <span className="playlist-screen__eq-bar" />
                                            <span className="playlist-screen__eq-bar" />
                                            <span className="playlist-screen__eq-bar" />
                                            <span className="playlist-screen__eq-bar" />
                                            <span className="playlist-screen__eq-bar" />
                                        </div>
                                    </div>

                                    <div className="playlist-screen__hero-body">
                                        {/* Vinilo o Gráfico Musical Giratorio */}
                                        <div className="playlist-screen__vinyl-wrapper">
                                            <div className="playlist-screen__vinyl-disc">
                                                <div className="playlist-screen__vinyl-grooves" />
                                                <div className="playlist-screen__vinyl-center">
                                                    <i className="bi bi-music-note text-white fs-2" />
                                                </div>
                                            </div>
                                            <div className="playlist-screen__vinyl-glow" />
                                        </div>

                                        {/* Información de la Canción */}
                                        <div className="playlist-screen__hero-meta">
                                            <h2 className="playlist-screen__song-title">
                                                {nowPlaying.title || nowPlaying.songTitle || 'Título de Canción'}
                                            </h2>
                                            <h3 className="playlist-screen__song-artist">
                                                {nowPlaying.artistName || nowPlaying.artist || 'Artista'}
                                            </h3>

                                            {/* Badges de Créditos y Votos */}
                                            <div className="playlist-screen__badges">
                                                {settings.showFanNameOnScreen !== false && nowPlaying.fanName && (
                                                    <div className="playlist-screen__badge playlist-screen__badge--fan">
                                                        <i className="bi bi-person-heart me-2" />
                                                        <span>Pedida por: <strong>{nowPlaying.fanName}</strong></span>
                                                    </div>
                                                )}

                                                {settings.showVoteCountOnScreen !== false && (Number(nowPlaying.voteCount) > 0) && (
                                                    <div className="playlist-screen__badge playlist-screen__badge--votes">
                                                        <i className="bi bi-fire me-2 text-warning" />
                                                        <span>
                                                            <strong>{nowPlaying.voteCount}</strong> {nowPlaying.voteCount === 1 ? 'voto de la afición' : 'votos de la afición'}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Tarjeta Lateral de QR para invitar a pedir */}
                                {settings.isScreenQrVisible !== false && (
                                    <div className="playlist-screen__side-qr-card">
                                        <div className="playlist-screen__side-qr-badge">¿QUIERES TU CANCIÓN?</div>
                                        <h4 className="playlist-screen__side-qr-title">¡Escanea y pídela!</h4>
                                        <div className="playlist-screen__qr-box playlist-screen__qr-box--side">
                                            <QRCodeSVG
                                                value={qrTargetUrl}
                                                size={180}
                                                bgColor="#ffffff"
                                                fgColor="#0a0f1d"
                                                level="H"
                                                imageSettings={qrCenterLogoSettings ? qrCenterLogoSettings(180) : undefined}
                                            />
                                        </div>
                                        <p className="playlist-screen__side-qr-footer">Elige el próximo tema del estadio</p>
                                    </div>
                                )}
                            </div>
                        ) : (
                            /* Standby elegante cuando no hay Now Playing */
                            <div className="playlist-screen__standby-card">
                                <div className="playlist-screen__standby-icon">
                                    <i className="bi bi-headphones" />
                                </div>
                                <h2 className="playlist-screen__standby-title">🎧 Preparando el siguiente tema...</h2>
                                <p className="playlist-screen__standby-subtitle">
                                    ¡Sé el próximo en sonar en el estadio! Escanea el código con tu celular y vota por tu favorita.
                                </p>
                                <div className="playlist-screen__qr-box playlist-screen__qr-box--standby">
                                    <QRCodeSVG
                                        value={qrTargetUrl}
                                        size={220}
                                        bgColor="#ffffff"
                                        fgColor="#0a0f1d"
                                        level="H"
                                        imageSettings={qrCenterLogoSettings ? qrCenterLogoSettings(220) : undefined}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* --------------------------------------------------------------------- */}
                {/* MODO 3: QUEUE (SPLIT LAYOUT CON CANCIÓN ACTUAL + PRÓXIMAS 3 EN COLA)  */}
                {/* --------------------------------------------------------------------- */}
                {currentMode === 3 && (
                    <div className="playlist-screen__queue">
                        <div className="playlist-screen__queue-split">
                            {/* Columna Izquierda: Now Playing Hero o Standby */}
                            <div className="playlist-screen__queue-left">
                                {nowPlaying ? (
                                    <div className="playlist-screen__hero-card playlist-screen__hero-card--split">
                                        <div className="playlist-screen__hero-header">
                                            <span className="playlist-screen__hero-pill">
                                                <i className="bi bi-disc-fill me-2" /> EN REPRODUCCIÓN
                                            </span>
                                            <div className="playlist-screen__eq-bars playlist-screen__eq-bars--small">
                                                <span className="playlist-screen__eq-bar" />
                                                <span className="playlist-screen__eq-bar" />
                                                <span className="playlist-screen__eq-bar" />
                                            </div>
                                        </div>

                                        <div className="playlist-screen__hero-body playlist-screen__hero-body--compact">
                                            <div className="playlist-screen__vinyl-disc playlist-screen__vinyl-disc--medium">
                                                <div className="playlist-screen__vinyl-grooves" />
                                                <div className="playlist-screen__vinyl-center">
                                                    <i className="bi bi-music-note text-white fs-3" />
                                                </div>
                                            </div>

                                            <div className="playlist-screen__hero-meta">
                                                <h2 className="playlist-screen__song-title playlist-screen__song-title--medium">
                                                    {nowPlaying.title || nowPlaying.songTitle || 'Título de Canción'}
                                                </h2>
                                                <h3 className="playlist-screen__song-artist playlist-screen__song-artist--medium">
                                                    {nowPlaying.artistName || nowPlaying.artist || 'Artista'}
                                                </h3>

                                                <div className="playlist-screen__badges">
                                                    {settings.showFanNameOnScreen !== false && nowPlaying.fanName && (
                                                        <div className="playlist-screen__badge playlist-screen__badge--fan">
                                                            <i className="bi bi-person-heart me-1" />
                                                            <span>{nowPlaying.fanName}</span>
                                                        </div>
                                                    )}
                                                    {settings.showVoteCountOnScreen !== false && (Number(nowPlaying.voteCount) > 0) && (
                                                        <div className="playlist-screen__badge playlist-screen__badge--votes">
                                                            <i className="bi bi-fire me-1 text-warning" />
                                                            <span>{nowPlaying.voteCount} votos</span>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="playlist-screen__standby-card playlist-screen__standby-card--compact">
                                        <i className="bi bi-music-note-list fs-1 text-cyan mb-2" />
                                        <h3 className="fw-bold text-white fs-4">Preparando temas...</h3>
                                        <p className="text-secondary small">Vota por las canciones que quieres escuchar</p>
                                    </div>
                                )}

                                {/* QR Card de participación rápida */}
                                {settings.isScreenQrVisible !== false && (
                                    <div className="playlist-screen__queue-qr-banner">
                                        <div className="playlist-screen__qr-box playlist-screen__qr-box--compact">
                                            <QRCodeSVG
                                                value={qrTargetUrl}
                                                size={130}
                                                bgColor="#ffffff"
                                                fgColor="#0a0f1d"
                                                level="H"
                                                imageSettings={qrCenterLogoSettings ? qrCenterLogoSettings(130) : undefined}
                                            />
                                        </div>
                                        <div className="playlist-screen__queue-qr-meta">
                                            <span className="playlist-screen__queue-qr-badge">¡PARTICIPA YA!</span>
                                            <h4 className="playlist-screen__queue-qr-title">Pide tu canción favorita</h4>
                                            <p className="playlist-screen__queue-qr-desc">Escanea y apoya con tus votos</p>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Columna Derecha: Cola de las siguientes canciones */}
                            <div className="playlist-screen__queue-right">
                                <div className="playlist-screen__queue-header">
                                    <span className="playlist-screen__queue-title">
                                        <i className="bi bi-list-ol me-2 text-warning" />
                                        SIGUIENTES EN COLA
                                    </span>
                                    <span className="playlist-screen__queue-counter">
                                        {nextQueueTracks.length} de {activePlaylist.filter(t => !t.isPlayed).length} canciones
                                    </span>
                                </div>

                                <div className="playlist-screen__queue-list">
                                    {nextQueueTracks.length > 0 ? (
                                        nextQueueTracks.map((track, idx) => {
                                            const trackId = track.id || track.suggestionId || idx;
                                            return (
                                                <div key={trackId} className="playlist-screen__queue-item">
                                                    <div className="playlist-screen__queue-rank">
                                                        <span className="playlist-screen__queue-rank-num">#{idx + 1}</span>
                                                        <span className="playlist-screen__queue-rank-tag">
                                                            {idx === 0 ? 'A CONTINUACIÓN' : 'EN ESPERA'}
                                                        </span>
                                                    </div>

                                                    <div className="playlist-screen__queue-item-meta">
                                                        <h4 className="playlist-screen__queue-item-title">
                                                            {track.title || track.songTitle || 'Título Desconocido'}
                                                        </h4>
                                                        <p className="playlist-screen__queue-item-artist">
                                                            {track.artistName || track.artist || 'Artista'}
                                                        </p>
                                                    </div>

                                                    <div className="playlist-screen__queue-item-badges">
                                                        {settings.showFanNameOnScreen !== false && track.fanName && (
                                                            <span className="playlist-screen__queue-fan-badge" title={track.fanName}>
                                                                <i className="bi bi-person-fill me-1" />
                                                                {track.fanName}
                                                            </span>
                                                        )}
                                                        {settings.showVoteCountOnScreen !== false && (Number(track.voteCount) > 0) && (
                                                            <span className="playlist-screen__queue-vote-badge">
                                                                <i className="bi bi-fire text-warning me-1" />
                                                                {track.voteCount}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="playlist-screen__queue-empty">
                                            <i className="bi bi-inbox fs-2 text-secondary mb-2" />
                                            <h4 className="text-white fs-5 fw-bold">¡La cola está libre!</h4>
                                            <p className="text-secondary small">Escanea el código QR y sé el primero en pedir un tema.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </main>

            {/* ========================================================================= */}
            {/* PIE DE PANTALLA: CINTILLO BROADCAST                                       */}
            {/* ========================================================================= */}
            <footer className="playlist-screen__footer">
                <div className="playlist-screen__footer-left">
                    <i className="bi bi-broadcast me-2 text-cyan" />
                    <span>INTERAKTIA V2 &bull; SISTEMA DE ENTRETENIMIENTO EN VIVO</span>
                </div>
                <div className="playlist-screen__footer-right">
                    <span>SEÑAL OFICIAL DEL ESTADIO</span>
                </div>
            </footer>
        </div>
    );
};

PlaylistScreen.propTypes = {
    roomId: PropTypes.string
};

export default PlaylistScreen;
